import {
  type ThemeRecipe,
  type ThemeTokens,
  buildThemeTokens
} from './build-theme-tokens';
import {
  COLOR_FAMILIES,
  COLOR_SHADES,
  type PaletteColor,
  paletteAlias,
  paletteColor
} from './palette';

/** The three files a committed theme folder holds. */
export type ThemeFiles = {
  'tokens-light.css': string;
  'tokens-dark.css': string;
  'tailwind-base.css': string;
};

/**
 * Every palette entry by the literal it resolves to.
 *
 * A list per literal, not one name. Exactly one colour in the palette is shared
 * — `oklch(0.985 0 0)` is `zinc-50`, `neutral-50` and `mauve-50` at once — and
 * picking whichever was seen last would label a theme's own grey with a family
 * it does not use.
 */
const paletteNames = new Map<string, Array<PaletteColor>>();
for (const family of COLOR_FAMILIES) {
  for (const step of COLOR_SHADES) {
    const name = `${family}-${step}` as PaletteColor;
    const literal = paletteColor(name);
    paletteNames.set(literal, [...(paletteNames.get(literal) ?? []), name]);
  }
}
for (const name of ['white', 'black'] as Array<PaletteColor>) {
  paletteNames.set(paletteColor(name), [name]);
}

/**
 * Put an override's palette colour back into alias form.
 *
 * Overrides arrive as literals — `parseTheme` rewrites them that way because a
 * theme injected at runtime cannot reference `--color-*`, which Tailwind only
 * emits for shades something referenced at build time. A committed file is the
 * opposite case: it is compiled, so the alias survives and says *which step was
 * chosen*, which a reviewer can judge and an oklch triple cannot.
 *
 * Whole values only. `oklch(0.21 0.006 285.786 / 5%)` is zinc-900 at five per
 * cent, and rewriting it to `var(--color-zinc-900)` would silently drop the
 * alpha.
 *
 * Where a colour has more than one name, the theme's own families win — a grey
 * in a zinc-based theme reads as `zinc-50`, not as whichever family the palette
 * happened to list last.
 */
const asAlias = (value: string, prefer: ReadonlyArray<string>): string => {
  const names = paletteNames.get(value.trim());
  if (!names?.length) {
    return value;
  }

  const chosen =
    prefer
      .map((family) => names.find((name) => name.startsWith(`${family}-`)))
      .find(Boolean) ?? names[0];

  return paletteAlias(chosen as PaletteColor);
};

const aliased = (
  tokens: ThemeTokens | undefined,
  prefer: ReadonlyArray<string>
): ThemeTokens =>
  Object.fromEntries(
    Object.entries(tokens ?? {}).map(([token, value]) => [
      token,
      asAlias(value, prefer)
    ])
  );

const block = (selector: string, tokens: ThemeTokens): string =>
  [
    `${selector} {`,
    ...Object.entries(tokens).map(([name, value]) => `  ${name}: ${value};`),
    '}',
    ''
  ].join('\n');

/**
 * Emit a theme as the files `theme-sync` reads.
 *
 * The counterpart to injecting a theme at runtime: dropped into
 * `libs/shared/theme/src/lib/<name>/` and added to `SITE_THEMES`, the same
 * recipe becomes a built-in that ships in the CSS bundle and goes through code
 * review.
 *
 * Palette colours are written as `var(--color-…)` here, unlike the runtime
 * form. These files are compiled, so the alias survives and says which palette
 * step was chosen — which a reviewer can judge and a literal oklch triple
 * cannot.
 *
 * `tokens-light.css` uses `:root` and `tokens-dark.css` uses `.dark`, never the
 * `[data-theme=dark]` attribute: once the attribute carries the theme name that
 * selector can no longer match, and `theme-sync` rejects a site theme written
 * that way.
 *
 * @param name - Folder and theme name, used in the header comment
 * @param recipe - The theme's recipe
 * @param overrides - Hand-edited tokens, applied last
 * @returns File contents keyed by filename
 */
export function themeFiles(
  name: string,
  recipe: ThemeRecipe,
  overrides: { light?: ThemeTokens; dark?: ThemeTokens } = {}
): ThemeFiles {
  const prefer = [recipe.baseFamily, recipe.brandFamily];
  const { light, dark } = buildThemeTokens(
    recipe,
    {
      light: aliased(overrides.light, prefer),
      dark: aliased(overrides.dark, prefer)
    },
    'alias'
  );

  return {
    'tokens-light.css': block(':root', light),
    'tokens-dark.css': block('.dark', dark),
    'tailwind-base.css': [
      `/* ${name} theme — generated by the theme studio */`,
      "@import './tokens-light.css';",
      "@import './tokens-dark.css';",
      '',
      '/* Core setup — variants, theme mappings, base layer */',
      "@import '../_core/tailwind-setup.css';",
      ''
    ].join('\n')
  };
}
